
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
    Shield,
    Lock,
    Eye,
    FileText,
    ArrowLeft,
    ChevronRight,
    Globe,
    Server,
    UserCheck,
    Gavel
} from 'lucide-react';
import { cn } from '@/lib/utils';

type LegalTab = 'privacy' | 'terms' | 'disclaimers' | 'security';

export default function Legal() {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<LegalTab>('privacy');

    useEffect(() => {
        const path = location.pathname.split('/').pop() as LegalTab;
        if (['privacy', 'terms', 'security', 'disclaimers'].includes(path)) {
            setActiveTab(path);
        }
        window.scrollTo(0, 0);
    }, [location]);

    const handleTabChange = (tab: LegalTab) => {
        setActiveTab(tab);
        navigate(`/legal/${tab}`);
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-300 font-medium selection:bg-emerald-500/30">
            {/* Background Effects */}
            <div className="fixed inset-0 z-0 pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-600/5 blur-[150px]" />
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/5 blur-[150px]" />
            </div>

            {/* Header */}
            <header className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-border">
                <div className="max-w-6xl mx-auto px-4 h-20 flex items-center justify-between">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/login')}
                        className="text-muted-foreground hover:text-foreground transition-colors gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to Home
                    </Button>

                    <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                            <span className="text-foreground font-semibold text-lg">D</span>
                        </div>
                        <span className="text-foreground font-semibold text-sm uppercase tracking-widest hidden sm:block">DCODE Private Limited</span>
                    </div>

                    <div className="w-[100px] hidden sm:block" />
                </div>
            </header>

            <main className="relative z-10 max-w-6xl mx-auto px-4 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
                    {/* Sidebar Navigation */}
                    <aside className="lg:col-span-1 space-y-2">
                        <button
                            onClick={() => handleTabChange('privacy')}
                            className={cn(
                                "w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300 group",
                                activeTab === 'privacy'
                                    ? "bg-emerald-600/10 border border-emerald-500/20 text-white"
                                    : "hover:bg-muted/50 text-muted-foreground"
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <Eye className={cn("h-5 w-5", activeTab === 'privacy' ? "text-emerald-500" : "text-muted-foreground group-hover:text-slate-300")} />
                                <span className="text-sm font-semibold uppercase tracking-widest">Privacy Policy</span>
                            </div>
                            {activeTab === 'privacy' && <ChevronRight className="h-4 w-4 text-emerald-500" />}
                        </button>

                        <button
                            onClick={() => handleTabChange('terms')}
                            className={cn(
                                "w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300 group",
                                activeTab === 'terms'
                                    ? "bg-emerald-600/10 border border-emerald-500/20 text-white"
                                    : "hover:bg-muted/50 text-muted-foreground"
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <FileText className={cn("h-5 w-5", activeTab === 'terms' ? "text-emerald-500" : "text-muted-foreground group-hover:text-slate-300")} />
                                <span className="text-sm font-semibold uppercase tracking-widest">Terms of Service</span>
                            </div>
                            {activeTab === 'terms' && <ChevronRight className="h-4 w-4 text-emerald-500" />}
                        </button>

                        <button
                            onClick={() => handleTabChange('disclaimers')}
                            className={cn(
                                "w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300 group",
                                activeTab === 'disclaimers'
                                    ? "bg-emerald-600/10 border border-emerald-500/20 text-white"
                                    : "hover:bg-muted/50 text-muted-foreground"
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <Gavel className={cn("h-5 w-5", activeTab === 'disclaimers' ? "text-emerald-500" : "text-muted-foreground group-hover:text-slate-300")} />
                                <span className="text-sm font-semibold uppercase tracking-widest">Disclaimers</span>
                            </div>
                            {activeTab === 'disclaimers' && <ChevronRight className="h-4 w-4 text-emerald-500" />}
                        </button>

                        <button
                            onClick={() => handleTabChange('security')}
                            className={cn(
                                "w-full flex items-center justify-between p-4 rounded-xl transition-all duration-300 group",
                                activeTab === 'security'
                                    ? "bg-emerald-600/10 border border-emerald-500/20 text-white"
                                    : "hover:bg-muted/50 text-muted-foreground"
                            )}
                        >
                            <div className="flex items-center gap-3">
                                <Shield className={cn("h-5 w-5", activeTab === 'security' ? "text-emerald-500" : "text-muted-foreground group-hover:text-slate-300")} />
                                <span className="text-sm font-semibold uppercase tracking-widest">Security</span>
                            </div>
                            {activeTab === 'security' && <ChevronRight className="h-4 w-4 text-emerald-500" />}
                        </button>

                        <div className="mt-8 p-6 rounded-2xl bg-slate-900/50 border border-border space-y-4">
                            <Lock className="h-8 w-8 text-emerald-500" />
                            <h4 className="text-foreground font-semibold text-xs uppercase tracking-widest">Trust Center</h4>
                            <p className="text-[11px] leading-relaxed text-muted-foreground uppercase tracking-wider font-medium">
                                DCODE is committed to maintaining the highest security and compliance standards for all our clients.
                            </p>
                        </div>
                    </aside>

                    {/* Content Area */}
                    <div className="lg:col-span-3">
                        <div className="prose prose-invert max-w-none">
                            {activeTab === 'privacy' && <PrivacyContent />}
                            {activeTab === 'terms' && <TermsContent />}
                            {activeTab === 'disclaimers' && <DisclaimerContent />}
                            {activeTab === 'security' && <SecurityContent />}
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-12 border-t border-border">
                <div className="max-w-6xl mx-auto px-4 text-center">
                    <p className="text-[10px] font-semibold text-slate-600 uppercase tracking-[0.5em]">
                        © 2024 DCODE PRIVATE LIMITED. ALL RIGHTS RESERVED.
                    </p>
                </div>
            </footer>
        </div>
    );
}

function Section({ title, icon: Icon, children }: { title: string; icon: unknown; children: React.ReactNode }) {
    return (
        <div className="mb-12 space-y-6">
            <div className="flex items-center gap-3 mb-6">
                <div className="h-10 w-10 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center justify-center">
                    <Icon className="h-5 w-5 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold text-foreground uppercase tracking-tight m-0">{title}</h3>
            </div>
            <div className="space-y-4 leading-relaxed text-muted-foreground">
                {children}
            </div>
        </div>
    );
}

function PrivacyContent() {
    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl font-semibold text-foreground uppercase tracking-tighter mb-4 m-0">Privacy Policy</h1>
            <p className="text-muted-foreground font-medium uppercase tracking-widest text-xs mb-12">Last Updated: March 2024</p>

            <Section title="Data Collection" icon={Globe}>
                <p>
                    DCODE Private Limited ("DCODE", "we", "us", or "our") is dedicated to protecting your personal data and privacy.
                    This policy describes how we collect, use, and handle your information when you use our services, including our estimation platform and corporate website.
                </p>
                <p>
                    We collect information that you provide directly to us, such as when you request a demo, create an account, or communicate with our team.
                    This includes your name, company details, email address, phone number, and any project-specific information provided for estimation purposes.
                </p>
            </Section>

            <Section title="Information Usage" icon={UserCheck}>
                <p>
                    We use the information we collect to provide, maintain, and improve our services, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>Generating accurate project estimates and proposals.</li>
                    <li>Managing your secure workspace and project history.</li>
                    <li>Communicating regarding service updates and project milestones.</li>
                    <li>Ensuring compliance with legal and regulatory requirements.</li>
                </ul>
            </Section>

            <Section title="Data Protection" icon={Shield}>
                <p>
                    We implement enterprise-grade security measures to safeguard your data. Your project data is encrypted both in transit and at rest using industry-standard protocols.
                </p>
                <p>
                    Access to sensitive project data is restricted to authorized personnel who require such access to provide our services. We do not sell your personal data to third parties.
                </p>
            </Section>

            <Section title="Your Rights" icon={Gavel}>
                <p>
                    You have the right to access, update, or delete your personal information at any time. If you wish to exercise these rights or have questions about our privacy practices,
                    please contact our data protection team at <strong>legal@dpldew.com</strong>.
                </p>
            </Section>
        </div>
    );
}

function TermsContent() {
    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl font-semibold text-foreground uppercase tracking-tighter mb-4 m-0">Terms of Service</h1>
            <p className="text-muted-foreground font-medium uppercase tracking-widest text-xs mb-12">Last Updated: March 2024</p>

            <Section title="Service Agreement" icon={FileText}>
                <p>
                    By accessing the DCODE platform or using our services, you agree to be bound by these Terms of Service.
                    These terms govern your use of our estimation software, project management tools, and any related services provided by DCODE Private Limited.
                </p>
            </Section>

            <Section title="Workspace Access" icon={Lock}>
                <p>
                    Access to the DCODE platform is restricted to authorized users. You are responsible for maintaining the confidentiality of your credentials
                    and for all activities that occur under your account. We reserve the right to terminate access for any violation of these terms.
                </p>
            </Section>

            <Section title="Intellectual Property" icon={Gavel}>
                <p>
                    All content, features, and functionality on the DCODE platform, including code, design, and estimation logic,
                    are the exclusive property of DCODE Private Limited and are protected by international copyright and trademark laws.
                </p>
            </Section>

            <Section title="Liability Limitations" icon={Shield}>
                <p>
                    While we strive for maximum accuracy in our estimation tools, DCODE provides services on an "as is" basis.
                    We are not liable for any discrepancies in project costs or timelines arising from external factors beyond our control,
                    such as market fluctuations or site-specific conditions.
                </p>
            </Section>
        </div>
    );
}

function DisclaimerContent() {
    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl font-semibold text-foreground uppercase tracking-tighter mb-4 m-0">Service Disclaimers</h1>
            <p className="text-muted-foreground font-medium uppercase tracking-widest text-xs mb-12">Essential Client Information</p>

            <Section title="Image Rendering" icon={Eye}>
                <p>
                    All 3D visualizations, renders, and images displayed on the DCODE platform or provided in proposals are for <strong>illustrative purposes only</strong>.
                    While we strive for high fidelity, the actual finished space may vary based on material availability, lighting conditions, and structural requirements.
                </p>
            </Section>

            <Section title="Material Variations" icon={Globe}>
                <p>
                    Natural materials like wood, stone, and marble exhibit inherent variations in grain, color, and texture.
                    Samples provided are indicative, and DCODE does not guarantee an exact match for materials with natural characteristics.
                </p>
            </Section>

            <Section title="Timelines & Estimates" icon={Gavel}>
                <p>
                    Project timelines and costing estimates provided by our tools are based on current market data and standard execution parameters.
                    Actual project costs and durations may be subject to change due to site-specific conditions, scope modifications, or force majeure events.
                </p>
            </Section>
        </div>
    );
}

function SecurityContent() {
    return (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h1 className="text-4xl font-semibold text-foreground uppercase tracking-tighter mb-4 m-0">Security Protocols</h1>
            <p className="text-muted-foreground font-medium uppercase tracking-widest text-xs mb-12">Verified Enterprise Infrastructure</p>

            <Section title="Infrastructure Security" icon={Server}>
                <p>
                    The DCODE platform is built on modern, secure cloud infrastructure. We leverage Supabase and Vercel's enterprise-grade security features,
                    including Row Level Security (RLS) and encrypted database connections.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                    <div className="p-4 rounded-xl bg-slate-900 border border-border space-y-2">
                        <div className="text-emerald-500 font-semibold text-[10px] uppercase tracking-widest">Encryption</div>
                        <p className="text-xs text-muted-foreground">AES-256 encryption at rest and TLS 1.3 for all data in transit.</p>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-900 border border-border space-y-2">
                        <div className="text-emerald-500 font-semibold text-[10px] uppercase tracking-widest">Authentication</div>
                        <p className="text-xs text-muted-foreground">Secure JWT-based authentication with optional Multi-Factor support.</p>
                    </div>
                </div>
            </Section>

            <Section title="Data Integrity" icon={Shield}>
                <p>
                    We maintain regular backups and point-in-time recovery capabilities to ensure your project data is never lost.
                    Our system architecture is designed for high availability and fault tolerance.
                </p>
            </Section>

            <Section title="Access Control" icon={Lock}>
                <p>
                    We implement strictly enforced access control policies. Only validated users can access specific project data,
                    enforced through backend security rules that cannot be bypassed by frontend modifications.
                </p>
            </Section>

            <Section title="Security Audits" icon={UserCheck}>
                <p>
                    DCODE undergoes regular internal security assessments and code reviews to identify and mitigate potential vulnerabilities.
                    We monitor our infrastructure 24/7 for unauthorized access attempts or suspicious activity.
                </p>
            </Section>
        </div>
    );
}
